import { useState } from "react";
import { toast } from "sonner";
import { Search, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { usePoll } from "@/hooks/usePoll";
import { fmtMoney, fmtDateTime, TX_STATUS } from "@/lib/format";
import { PageHeader, StatusBadge, Empty } from "@/components/kit";

const ReviewDialog = ({ tx, onClose, onDone }) => {
  const [action, setAction] = useState("confirm");
  const [note, setNote] = useState("");
  const [ext, setExt] = useState("");
  const submit = async () => {
    try { await api.post(`/admin/transactions/${tx.id}/review`, { action, note: note || null, external_ref: ext || null }); toast.success("Transação atualizada"); onDone(); onClose(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="review-tx-dialog">
        <DialogHeader><DialogTitle>Analisar {tx.type === "deposit" ? "depósito" : "saque"} {tx.reference}</DialogTitle></DialogHeader>
        <div className="space-y-1 text-sm rounded-xl border border-border p-4">
          <div className="flex justify-between"><span className="text-muted-foreground">Cliente</span><span>{tx.user_name} ({tx.client_code})</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">Valor</span><span className="num font-semibold">{fmtMoney(tx.amount, tx.currency)}</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">{tx.type === "deposit" ? "Método" : "Destino"}</span><span>{tx.method || tx.destination}</span></div>
          {tx.risk_flag && <div className="flex gap-2 text-amber-500 pt-1"><AlertTriangle className="h-4 w-4" />Valor elevado — requer análise de risco</div>}
        </div>
        <div className="space-y-3">
          <Select value={action} onValueChange={setAction}><SelectTrigger className="h-11" data-testid="review-action-select"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="confirm">Confirmar</SelectItem><SelectItem value="review">Colocar em análise</SelectItem><SelectItem value="reject">Recusar</SelectItem></SelectContent></Select>
          {action === "confirm" && tx.type === "deposit" && (
            <div className="space-y-1.5"><Label>Referência externa do pagamento (conciliação)</Label>
              <Input className="h-11" value={ext} onChange={(e) => setExt(e.target.value)} placeholder="ID no provedor/banco" data-testid="review-external-ref-input" />
              <p className="text-xs text-muted-foreground">Confirme somente após verificar o recebimento no extrato do provedor. Comprovantes enviados pelo cliente não bastam.</p></div>
          )}
          <Textarea placeholder="Observação interna" value={note} onChange={(e) => setNote(e.target.value)} data-testid="review-note-input" />
        </div>
        <DialogFooter><Button onClick={submit} data-testid="review-submit-btn">Aplicar</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function AdminFinance() {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("open");
  const [type, setType] = useState("all");
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);
  const load = () => api.get("/admin/transactions", { params: { status: ["all", "open"].includes(status) ? undefined : status, type: type === "all" ? undefined : type, q: q || undefined } })
    .then((r) => setRows(status === "open" ? r.data.filter((t) => ["requested", "under_review"].includes(t.status)) : r.data));
  usePoll(() => load().catch(() => {}), 15000, [status, type, q]);
  return (
    <div data-testid="admin-finance-page">
      <PageHeader eyebrow="Financeiro" title="Depósitos, saques e transações" desc="Analise solicitações, confirme com referência externa e mantenha o livro conciliado. Webhook assinado disponível em /api/webhooks/payments." />
      <div className="flex flex-col md:flex-row gap-3 mb-5">
        <div className="relative flex-1"><Search className="absolute left-3 top-3.5 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Referência, cliente ou identificador" className="h-11 pl-9" value={q} onChange={(e) => setQ(e.target.value)} data-testid="tx-search-input" /></div>
        <Select value={status} onValueChange={setStatus}><SelectTrigger className="h-11 md:w-52" data-testid="tx-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="open">Aguardando análise</SelectItem><SelectItem value="all">Todos</SelectItem>{Object.entries(TX_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent></Select>
        <Select value={type} onValueChange={setType}><SelectTrigger className="h-11 md:w-44" data-testid="tx-type-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Depósitos e saques</SelectItem><SelectItem value="deposit">Depósitos</SelectItem><SelectItem value="withdrawal">Saques</SelectItem></SelectContent></Select>
      </div>
      <div className="surface overflow-x-auto">
        <table className="w-full text-sm" data-testid="admin-tx-table">
          <thead><tr className="text-left eyebrow border-b border-border"><th className="py-3 pl-5">Referência</th><th>Cliente</th><th>Tipo</th><th className="text-right">Valor</th><th>Status</th><th>Data</th><th /></tr></thead>
          <tbody>{rows.map((t) => (
            <tr key={t.id} className="border-b border-border" data-testid={`admin-tx-row-${t.id}`}>
              <td className="py-3 pl-5 num text-xs">{t.reference}{t.external_ref && <div className="text-muted-foreground">Ext: {t.external_ref}</div>}</td>
              <td><div className="font-medium">{t.user_name}</div><div className="num text-xs text-primary">{t.client_code}</div></td>
              <td>{t.type === "deposit" ? "Depósito" : "Saque"}{t.risk_flag && <AlertTriangle className="inline ml-1 h-3.5 w-3.5 text-amber-500" />}</td>
              <td className="text-right num font-semibold">{fmtMoney(t.amount, t.currency)}</td>
              <td><StatusBadge map={TX_STATUS} value={t.status} /></td>
              <td className="text-xs text-muted-foreground">{fmtDateTime(t.created_at)}</td>
              <td className="pr-4 text-right">{["requested", "under_review"].includes(t.status) && <Button size="sm" onClick={() => setSel(t)} data-testid={`admin-tx-review-${t.id}`}>Analisar</Button>}</td>
            </tr>))}</tbody>
        </table>
        {rows.length === 0 && <Empty text="Nenhuma transação." />}
      </div>
      {sel && <ReviewDialog tx={sel} onClose={() => setSel(null)} onDone={load} />}
    </div>
  );
}
